<template>
  <div class="vehicle-info modal-content-scrollable">
    <div v-if="loading" class="loading-container">
      <p>{{ $t('Loading vehicle information...') }}</p>
      <div class="spinner"></div>
    </div>

    <div v-else>
      <div class="vehicle-info-icons">
        <button
            type="button"
            class="vi-icon-btn"
            :class="{ 'is-active': showSensors }"
            :title="$t('Show Sensor Status')"
            @click="toggleSensors"
        >
          <i class="fa fa-signal"></i>
        </button>
        <button
            type="button"
            class="vi-icon-btn"
            :class="{ 'is-active': showGpsInfo }"
            :title="$t('Show GPS info')"
            @click="toggleGpsInfo"
        >
          <i class="fa fa-route"></i>
        </button>
        <button
            v-if="vehicleId && enableCameraButton"
            type="button"
            class="vi-icon-btn"
            :title="$t('Open Camera')"
            @click="$emit('open-camera', vehicleId)"
        >
          <i class="fa fa-video"></i>
        </button>
        <button
            v-if="vehicleId && enableDevicesButton"
            type="button"
            class="vi-icon-btn"
            :title="$t('Devices')"
            @click="$emit('open-devices', vehicleId)"
        >
          <i class="fa fa-microchip"></i>
        </button>
      </div>

      <div class="vehicle-details vi-section">
        <div class="vi-vehicle-name"><i class="fa fa-car"></i> {{ displayVehicleName }}</div>
        <ul>
          <li><span class="vi-detail-label"><i class="fa fa-id-card"></i> {{ $t('Plate') }}</span><strong>{{ displayLicensePlate }}</strong></li>
          <li><span class="vi-detail-label"><i class="fa fa-truck"></i> {{ $t('Type') }}</span><strong>{{ selectedVehicleTypeName }}</strong></li>
          <li><span class="vi-detail-label"><i class="fa fa-building"></i> {{ $t('Unit') }}</span><strong>{{ selectedUnitName }}</strong></li>
          <li v-if="gps.speed !== undefined">
            <span class="vi-detail-label"><i class="fa fa-tachometer-alt"></i> {{ $t('Speed') }}</span><strong>{{ gps.speed }} Km/h</strong>
          </li>
          <li v-if="gps.sentDate">
            <span class="vi-detail-label"><i class="fa fa-clock"></i> {{ $t('Sent date') }}</span><strong>{{ convertToLocalTime(gps.sentDate) }}</strong>
          </li>
          <li v-for="device in devices" :key="device.id">
            <span class="vi-detail-label"><i class="fa fa-microchip"></i> {{ device.label }}</span>
            <span :class="['status-badge', device.detachment_status === 'attached' ? 'status-green' : 'status-red']">
              {{ device.detachment_status === 'attached' ? $t('OK') : $t('Not located') }}
            </span>
          </li>
        </ul>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="sensor-status">
            <div v-if="showSensors" class="sensors-list vi-section">
              <div class="vi-section-title">{{ $t('Sensores') }}</div>
              <ul>
                <li v-if="sensors.battery !== undefined">
                  <span>{{ $t('Vehicle battery') }}</span><span :class="['status-badge', getStatusClass(sensors.battery)]">{{ getStatusLabel(sensors.battery) }}</span>
                </li>
                <li v-if="hasOdometer && sensors.hood !== undefined">
                  <span>{{ $t('Closed hood') }}</span><span :class="['status-badge', getStatusClass(sensors.hood)]">{{ getStatusLabel(sensors.hood) }}</span>
                </li>
                <li v-if="sensors.communication !== undefined">
                  <span>{{ $t('Communication') }}</span><span :class="['status-badge', getStatusClass(sensors.communication)]">{{ getStatusLabel(sensors.communication) }}</span>
                </li>
                <li v-if="sensors.moving !== undefined">
                  <span>{{ $t('Moving') }}</span><span :class="['status-badge', getStatusClass(sensors.moving)]">{{ getStatusLabel(sensors.moving) }}</span>
                </li>
                <li v-if="hasOdometer && sensors.ignition !== undefined">
                  <span>{{ $t('Ignition') }}</span><span :class="['status-badge', getStatusClass(sensors.ignition)]">{{ getStatusLabel(sensors.ignition) }}</span>
                </li>
                <li v-if="sensors.batteryLevel">
                  <span>{{ $t('Battery level') }}</span><strong>{{ sensors.batteryLevel }}</strong>
                </li>
                <li v-if="hasOdometer && sensors.driverDoor !== undefined">
                  <span>{{ $t('Driver door closed') }}</span><span :class="['status-badge', getStatusClass(sensors.driverDoor)]">{{ getStatusLabel(sensors.driverDoor) }}</span>
                </li>
                <li v-if="hasOdometer && sensors.passengerDoor !== undefined">
                  <span>{{ $t('Passenger door closed') }}</span><span :class="['status-badge', getStatusClass(sensors.passengerDoor)]">{{ getStatusLabel(sensors.passengerDoor) }}</span>
                </li>
                <li v-if="sensors.gpsSignal !== undefined">
                  <span>{{ $t('GPS signal') }}</span><span :class="['status-badge', getStatusClass(sensors.gpsSignal)]">{{ getStatusLabel(sensors.gpsSignal) }}</span>
                </li>
                <li v-if="hasSpeed">
                  <span>{{ $t('Speed over 100 km/h') }}</span><span :class="['status-badge', getStatusClass(sensors.speedOver100)]">{{ getStatusLabel(sensors.speedOver100) }}</span>
                </li>
                <li v-if="hasSpeed">
                  <span>{{ $t('Speed over 60 km/h') }}</span><span :class="['status-badge', getStatusClass(sensors.speedOver60)]">{{ getStatusLabel(sensors.speedOver60) }}</span>
                </li>
                <li v-if="hasSpeed">
                  <span>{{ $t('Speed over 80 km/h') }}</span><span :class="['status-badge', getStatusClass(sensors.speedOver80)]">{{ getStatusLabel(sensors.speedOver80) }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="gps-info">
            <div v-if="showGpsInfo" class="gps-list vi-section">
              <div class="vi-section-title">GPS</div>
              <ul>
                <li v-if="gps.position && gps.position !== '0,0'">
                  <span class="vi-detail-label"><i class="fa fa-map-marker-alt"></i> {{ $t('Position') }}</span><strong>{{ gps.position }}</strong>
                </li>
                <li v-if="gps.date">
                  <span class="vi-detail-label"><i class="fa fa-clock"></i> {{ $t('GPS date') }}</span><strong>{{ convertToLocalTime(gps.date) }}</strong>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: "VehicleInfo",
  props: {
    vehicle: Object,
    vehicleDatalhe: Object,
    enableDevicesButton: {
      type: Boolean,
      default: false,
    },
    enableCameraButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSensors: false,
      showGpsInfo: false,
      vehicleTypes: {},
      units: {},
      lastUpdateData: null,
      vehicleDetalheLocal: this.vehicleDatalhe || {},
      loading: true,
      devices: [],
    }
  },
  computed: {
    displayVehicleName() {
      const currentVehicle = this.vehicleDatalhe?.vehicle || this.vehicleDetalheLocal?.vehicle
      if (!currentVehicle) return 'Desconhecido'
      if (typeof currentVehicle === 'string') return currentVehicle
      if (typeof currentVehicle === 'object') return currentVehicle.name || JSON.stringify(currentVehicle)
      return 'Desconhecido'
    },
    displayLicensePlate() {
      return this.vehicleDetalheLocal?.vehicle?.license_plate
          ?? this.vehicleDetalheLocal?.license_plate
          ?? this.vehicleDatalhe?.license_plate
          ?? 'Desconhecida'
    },
    selectedVehicleTypeName() {
      const id = this.vehicleDetalheLocal?.vehicle?.vehicle_type_id
      return this.vehicleTypes[id] ?? this.vehicleDetalheLocal?.vehicleType ?? 'Desconhecido'
    },
    selectedUnitName() {
      const id = this.vehicleDetalheLocal?.vehicle?.unit_id ?? this.vehicle?.unit_id
      return this.units[id] ?? this.vehicleDetalheLocal?.unit ?? 'Desconhecido'
    },
    lastUpdateTime() {
      return this.lastUpdateData?.updated_at ?? null
    },
    vehicleId() {
      return this.vehicleDetalheLocal?.vehicle?.id
          ?? this.vehicleDatalhe?.vehicle_id
          ?? this.vehicleDatalhe?.vehicle?.id
          ?? null
    },
    gps() {
      if (!this.lastUpdateData && !this.vehicle) return {
        position: '0,0',
        date: '',
        sentDate: '',
        satellites: '',
        speed: 0,
        altitude: '',
        azimuth: ''
      }

      const latitude = this.vehicle?.lat || this.vehicle?.latitude || 0
      const longitude = this.vehicle?.lng || this.vehicle?.long || this.vehicle?.lon || 0
      const trackingDate = this.lastUpdateData?.last_tracking_at || this.lastUpdateData?.created_at || ''

      return {
        position: `${latitude},${longitude}`,
        date: trackingDate,
        sentDate: trackingDate,
        satellites: this.vehicle?.sats || '',
        speed: this.lastUpdateData?.speed || 0,
        altitude: this.vehicle?.altitude || '',
        azimuth: this.vehicle?.angle || ''
      }
    },
    sensors() {
      if (!this.lastUpdateTime || !this.lastUpdateData) return {
        battery: false,
        communication: false,
        gpsSignal: false,
        moving: false,
        batteryLevel: 'N/A'
      }

      const now = new Date()
      const lastUpdate = new Date(this.lastUpdateTime)
      const diffMins = (now - lastUpdate) / 60000
      const hasOdometer = this.lastUpdateData?.odometer != null

      return {
        battery: this.getVehicleBatteryStatus(),
        hood: this.getHoodStatus(),
        communication: diffMins <= 5,
        moving: this.getMovingStatus(),
        ignition: this.getIgnitionStatus(),
        batteryLevel: this.getMainBatteryDec(this.lastUpdateData?.device_battery_level),
        driverDoor: this.getDoorStatus('driver'),
        passengerDoor: this.getDoorStatus('passenger'),
        gpsSignal: this.getGpsSignalStatus(),
        speedOver100: this.getSpeedStatus(100),
        speedOver60: this.getSpeedStatus(60),
        speedOver80: this.getSpeedStatus(80)
      }
    },
    hasOdometer() {
      return this.lastUpdateData?.odometer != null
    },
    hasSpeed() {
      return this.lastUpdateData?.speed != null
    }
  },
  methods: {
    getVehicleBatteryStatus() {
      if (!this.vehicle?.VehicleBattery) return false
      return this.vehicle.VehicleBattery !== "ON"
    },
    getHoodStatus() {
      if (!this.hasOdometer) return undefined
      return this.lastUpdateData.hood_status === 0
    },
    getMovingStatus() {
      if (!this.lastUpdateData?.speed) return false
      return Number(this.lastUpdateData.speed) > 0
    },
    getIgnitionStatus() {
      if (!this.hasOdometer) return undefined
      return this.lastUpdateData.ignition_status === 0
    },
    getDoorStatus(type) {
      if (!this.hasOdometer) return undefined
      return this.lastUpdateData[`${type}_door_status`] === 0
    },
    getGpsSignalStatus() {
      if (!this.vehicle?.gps) return false
      return this.vehicle.gps !== "NOFIX"
    },
    getSpeedStatus(limit) {
      if (!this.hasSpeed) return undefined
      return Number(this.lastUpdateData.speed) > limit
    },
    getMainBatteryDec(val) {
      if (!val) return 'N/A'
      return val > 100 ? (val / 10).toFixed(1) : val
    },
    getStatusClass(status) {
      if (status === undefined) return ''
      return status ? 'status-green' : 'status-red'
    },
    getStatusLabel(status) {
      return status ? 'OK' : 'Alerta'
    },
    toggleSensors() {
      this.showSensors = !this.showSensors
    },
    toggleGpsInfo() {
      this.showGpsInfo = !this.showGpsInfo
    },
    convertToLocalTime(dateString) {
      if (!dateString || typeof dateString !== 'string') return ''

      try {
        if (!dateString.includes('T') && !dateString.includes('Z')) {
          const [datePart, timePart] = dateString.split(' ')
          const [year, month, day] = datePart.split('-')
          const [hours, minutes, seconds] = timePart.split(':')
          return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
        }

        const dateObject = new Date(dateString)
        if (isNaN(dateObject.getTime())) return dateString

        const day = String(dateObject.getDate()).padStart(2, '0')
        const month = String(dateObject.getMonth() + 1).padStart(2, '0')
        const year = dateObject.getFullYear()
        const hours = String(dateObject.getHours()).padStart(2, '0')
        const minutes = String(dateObject.getMinutes()).padStart(2, '0')
        const seconds = String(dateObject.getSeconds()).padStart(2, '0')

        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
      } catch (error) {
        return dateString
      }
    },
    async getVehicleTypes() {
      if (Object.keys(this.vehicleTypes).length) return
      try {
        const response = await axios.get(this.$store.state.api + 'vehicle_types')
        if (!response.data?.data) return
        response.data.data.forEach(vehicleType => this.$set(this.vehicleTypes, vehicleType.id, vehicleType.name))
      } catch (error) {
        console.error('Erro ao carregar vehicle types:', error)
      }
    },
    async getUnits() {
      if (Object.keys(this.units).length) return
      try {
        const response = await axios.get(this.$store.state.api + 'units')
        if (!response.data?.data) return
        response.data.data.forEach(unit => this.$set(this.units, unit.id, unit.name))
      } catch (error) {
        console.error('Erro ao carregar units:', error)
      }
    },
    async fetchVehicleById(vehicleId) {
      if (!vehicleId) return
      try {
        const response = await axios.get(`${this.$store.state.api}vehicles/${vehicleId}`)
        const bruto = response.data
        if (!bruto) return

        const encontrado = Array.isArray(bruto.data) ? bruto.data[0] : (bruto.data ?? bruto)
        if (!encontrado) return

        const anterior = this.vehicleDetalheLocal?.vehicle
        this.vehicleDetalheLocal = {
          ...this.vehicleDetalheLocal,
          vehicle: {
            ...(typeof anterior === 'object' && anterior !== null ? anterior : {}),
            ...encontrado,
          },
        }
      } catch (error) {
        console.error('Erro fetch vehicle by id:', error)
      }
    },
    async getLastUpdate(vehicleId) {
      if (!vehicleId) return
      try {
        const response = await axios.get(`${this.$store.state.api}vehicle_last_updates/vehicle/${vehicleId}`)
        this.lastUpdateData = response.data.data ?? response.data
      } catch (error) {
        console.error('Erro fetch last update:', error)
        this.lastUpdateData = null
      }
    },
    async getDevices(vehicleId) {
      if (!vehicleId) return
      try {
        const response = await axios.get(`${this.$store.state.api}vehicles/${vehicleId}/devices/positions`)
        this.devices = response.data.data ?? []
      } catch (error) {
        console.error('Erro fetch vehicle devices:', error)
        this.devices = []
      }
    }
  },
  watch: {
    vehicleDatalhe: {
      immediate: true,
      deep: true,
      handler(novo) {
        if (!novo) return

        const anterior = this.vehicleDetalheLocal?.vehicle
        const manterAnterior = typeof anterior === 'object' && anterior !== null

        this.vehicleDetalheLocal = {
          ...novo,
          vehicle: manterAnterior ? anterior : novo.vehicle
        }
      }
    },
    'vehicleDatalhe.vehicle.id': {
      immediate: true,
      async handler(newId) {
        const vehicleId = newId ?? this.$route.params.id
        if (!vehicleId) return
        this.loading = true
        try {
          await Promise.all([
            this.getVehicleTypes(),
            this.getUnits(),
            this.fetchVehicleById(vehicleId),
            this.getLastUpdate(vehicleId),
            this.getDevices(vehicleId)
          ])
        } finally {
          this.loading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.vehicle-info-icons {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.vi-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid #333;
  border-radius: 6px;
  background: #fff;
  color: #1a1a1a;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
}

.vi-icon-btn:hover {
  background: #f1f1f1;
}

.vi-icon-btn.is-active {
  background: #007bff;
  border-color: #007bff;
  color: #fff;
}

.vehicle-info {
  font-family: Arial, sans-serif;
  font-size: 15px;
  width: 100%;
  color: #333;
}

.vehicle-details h3 {
  margin: 0 0 10px;
  font-size: 1.2em;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0 0 15px;
}

li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 8px 0;
  font-size: 14px;
  line-height: 1.4;
}

.sensors-list ul {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px 32px;
}

.vi-section {
  padding: 14px 0;
  border-top: 1px solid #e9ecef;
}

.vi-section-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8f98;
  margin-bottom: 10px;
}

.vi-vehicle-name {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 10px;
}

.vi-vehicle-name .fa {
  color: #007bff;
  margin-right: 6px;
}

.vi-detail-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #555;
}

.vi-detail-label .fa {
  width: 16px;
  text-align: center;
  color: #8a8f98;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
}

.status-badge.status-green {
  background: #28a745;
}

.status-badge.status-red {
  background: #dc3545;
}

button {
  margin: 8px 0;
  padding: 6px 12px;
  font-size: 0.85em;
  cursor: pointer;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  user-select: none;
}

button:hover {
  background-color: #0056b3;
}

.loading-container {
  text-align: center;
  padding: 25px 0;
  font-weight: 600;
  font-size: 1em;
  color: #555;
}

h3 {
  color: #555;
}

.spinner {
  margin: 15px auto 0;
  width: 28px;
  height: 28px;
  border: 4px solid #007bff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
